import { toast } from "@arcadeai/design-system/components/ui/molecules/toast";
import { createQueryClient } from "@arcadeai/ui-kit/query-client";
import { isFormOwnedError } from "./form-server-errors";
import {
  isAuthzDenial,
  isHttpError,
  isValidationError,
  parseApiError,
} from "./http-error";
import { isNetworkError } from "./network-error";

const NETWORK_RETRY_LIMIT = 3;
const NETWORK_RETRY_DELAY_MS = 500;
const DEFAULT_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 5000;

export const queryClient = createQueryClient({
  onMutationError: (error, errorMessage = "An error occurred") => {
    // A 403 may mean the caller's permissions changed; refetch the authoritative
    // set. Invalidate every capabilities entry (the "capabilities" key prefix covers
    // both org and project scopes) on purpose: the change could be at either scope,
    // and refetching both is cheap, so there's no need to narrow it.
    if (isHttpError(error) && error.status === 403) {
      queryClient.invalidateQueries({ queryKey: ["capabilities"] });
    }

    if (isValidationError(error)) {
      // A form showing this rejection on its inputs claims it, which happens in
      // the mutation's own onError — React Query runs this handler first, so the
      // decision waits a tick. Whatever no form claims still gets a toast: most
      // mutations here are not wired to a form, and every validated engine
      // endpoint can answer with named fields.
      setTimeout(() => {
        if (!isFormOwnedError(error)) {
          toast.error(parseApiError(error));
        }
      }, 0);
      return;
    }

    if (isHttpError(error) || error instanceof Error) {
      toast.error(parseApiError(error));
    } else {
      toast.error(errorMessage);
    }
  },
  shouldRetry: (failureCount, error) => {
    // A settled answer is deterministic for a given caller and resource, so a
    // retry can only repeat it — it doubles the failed requests behind a single
    // page load with no chance of a different outcome. Scoped to an authorization
    // denial rather than every 403/404: an unrelated miss that happens to share
    // the status keeps the retry, since that one may genuinely be transient.
    if (isHttpError(error) && error.status === 401) {
      return false;
    }
    if (isAuthzDenial(error)) {
      return false;
    }
    if (isNetworkError(error)) {
      return failureCount < NETWORK_RETRY_LIMIT;
    }
    return failureCount < 1;
  },
  retryDelay: (attemptIndex, error) => {
    if (isNetworkError(error)) {
      return (attemptIndex + 1) * NETWORK_RETRY_DELAY_MS;
    }
    return Math.min(
      DEFAULT_RETRY_DELAY_MS * 2 ** attemptIndex,
      MAX_RETRY_DELAY_MS
    );
  },
});
